import { sortField } from "@smart/itops-utils-basic";

import { groupLength } from "./render-ordered-items";
import { Visibility } from "./visibility";
import { FieldItem, GroupItem, OrderedItem, SectionItem } from "../types";

export type OrderedItemsOptions = {
  selected?: SectionItem;
  groups: GroupItem[];
  fields: FieldItem[];
  visibility?: Visibility;
  responses: Record<string, any>;
};

export const useOrderedItems = ({
  selected,
  groups,
  fields,
  visibility,
  responses,
}: OrderedItemsOptions): OrderedItem[] => {
  if (!selected) return [];

  const orderedGroups: OrderedItem[] = groups
    .filter(
      (g) =>
        g.sectionUri === selected.uri &&
        (!visibility || !!visibility.visibleGroups[g.uri]),
    )
    .map((group) => {
      const groupOrderedFields = sortField(
        fields.filter(
          (f) =>
            f.sectionUri === selected.uri &&
            f.groupUri === group.uri &&
            (!visibility || !!visibility.visibleFields[f.uri]),
        ),
        { key: "order", dir: "asc" },
      );
      // Generate repeatable groups
      const length = groupLength({
        group,
        fields: groupOrderedFields,
        responses,
      });
      const groupFieldSets = Array.from({ length }, (_, index) => ({
        visibleFields: groupOrderedFields.filter(
          (gf) =>
            visibility?.visibleRepeatableFields[gf.uri]?.[index] !== false,
        ),
        index,
      })).filter((gf) => !!gf.visibleFields.length);

      return {
        group,
        fields: groupOrderedFields,
        type: "group" as const,
        order: group.order,
        groupFieldSets,
      };
    });

  const orderedFields: OrderedItem[] = fields
    .filter(
      (f) =>
        f.sectionUri === selected.uri &&
        !f.groupUri &&
        (!visibility || !!visibility.visibleFields[f.uri]),
    )
    .map((field) => ({
      field,
      type: "field",
      order: field.order,
    }));

  return sortField([...orderedFields, ...orderedGroups], {
    key: "order",
    dir: "asc",
  });
};
